import { getCurrencySymbol } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  viewChild,
} from '@angular/core';
import { ChangeContext, Options as RangeSliderOptions, SliderComponent } from 'ngx-slider-v2';
import { BehaviorSubject, Subscription, debounceTime, filter } from 'rxjs';
import { InputNumberComponent } from '../input-number/input-number.component';
import { Interaction } from './range-slider.interface';

@Component({
  selector: 'rx-range-slider',
  templateUrl: './range-slider.component.html',
  styleUrls: ['./range-slider.component.scss'],
})
export class RangeSliderComponent implements OnDestroy, OnInit, AfterViewInit {
  /**
   * Deafult selected range
   */
  @Input() selectedRange: [number, number];
  /**
   * Currency to display
   */
  @Input() selectedCurrency: string;
  /**
   * Shows the inputs for min-max below the slider
   */
  @Input() showInputs = false;
  /**
   * Minimum value
   */
  @Input() minValue: number;
  /**
   * Maximum value
   */
  @Input() maxValue: number;
  /**
   * Options object (check notes for more info)
   */
  @Input({ required: true }) options: RangeSliderOptions;
  /**
   * On range selection
   */
  @Output() rangeSelect = new EventEmitter<any>();

  /**
   * Send data
   */
  @Output() interactions = new EventEmitter<Interaction>();

  @ViewChild('inputMaxValue') inputMaxValue: InputNumberComponent;
  @ViewChild('inputMinValue') inputMinValue: InputNumberComponent;

  slider = viewChild<SliderComponent>('slider');

  #valueChange$: BehaviorSubject<null | {
    min: number;
    max: number;
    interactionWithMin: boolean;
    interactionWithMax: boolean;
  }> = new BehaviorSubject<null | {
    min: number;
    max: number;
    interactionWithMin: boolean;
    interactionWithMax: boolean;
  }>(null);
  valueChange$ = this.#valueChange$.pipe(
    filter((changeContext) => !!changeContext || !!changeContext.max || !!changeContext.min),
    debounceTime(1000),
  );

  subscription: Subscription = new Subscription();

  ngOnInit(): void {
    //TODO make the options required with angular upgrade ticket STAY-998
    if (this.selectedRange || this.options) {
      this.#valueChange$.next({
        min: this.selectedRange ? this.selectedRange[0] : this.options.floor,
        max: this.selectedRange ? this.selectedRange[1] : this.options.ceil,
        interactionWithMin: false,
        interactionWithMax: false,
      });
    }

    this.subscription.add(
      this.valueChange$.subscribe((values) => {
        if (values.interactionWithMax || values.interactionWithMin) {
          this.interactions.emit({
            interactWithMaxInput: values.interactionWithMax,
            interactWithMinInput: values.interactionWithMin,
          });
        }
        const normalizedMin = values.interactionWithMin ? this.normalizeMinValue(values.min) : values.min;
        const normalizedMax = values.interactionWithMax ? this.normalizeMaxValue(values.max) : values.max;
        this.rangeSelect.emit({
          minValue: normalizedMin,
          maxValue: normalizedMax,
        });
        this.minValue = normalizedMin;
        this.maxValue = normalizedMax;
        if (this.inputMaxValue && this.inputMinValue) {
          this.inputMinValue.value = this.minValue;
          this.inputMaxValue.value = this.maxValue;
        }
      }),
    );
  }

  onUserChangeEnd(changeContext: ChangeContext): void {
    const range = {
      minValue: changeContext.value,
      maxValue: changeContext.highValue,
    };
    this.rangeSelect.emit({ ...{}, ...range });
  }

  changeMinValue(value: number): void {
    if (value !== null) {
      this.#valueChange$.next({
        min: value,
        max: this.maxValue,
        interactionWithMax: false,
        interactionWithMin: true,
      });
    }
  }

  changeMaxValue(value: number): void {
    if (value !== null) {
      this.#valueChange$.next({
        min: this.minValue,
        max: value,
        interactionWithMax: true,
        interactionWithMin: false,
      });
    }
  }

  onMinBlur(value: number, element: InputNumberComponent): void {
    if (value === null) {
      element.value = this.minValue;
      this.changeMinValue(this.minValue);
    }
  }

  onMaxBlur(value: number, element: InputNumberComponent): void {
    if (value === null) {
      element.value = this.maxValue;
      this.changeMaxValue(this.maxValue);
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  normalizeMaxValue(maxValue: number): number {
    if (maxValue > this.options.ceil) {
      this.interactions.emit({
        setMaxAboveCeil: true,
      });
      return this.options.ceil;
    }
    if (maxValue < this.minValue) {
      this.interactions.emit({
        correctedMaxBelowMin: true,
      });
      return this.minValue + 10;
    }

    return maxValue;
  }

  normalizeMinValue(minValue: number): number {
    if (minValue <= this.options.floor) {
      this.interactions.emit({
        setMinBelowFloor: true,
      });
      return this.options.floor;
    }
    if (minValue >= this.maxValue) {
      this.interactions.emit({
        correctedMinAboveMax: true,
      });
      return this.maxValue - 10;
    }

    return minValue;
  }

  getCurrencySymbol(currencyCode: string): string {
    const currencySymbol = getCurrencySymbol(currencyCode, 'narrow');
    return currencyCode === 'CAD' ? `CAD${currencySymbol}` : currencySymbol;
  }

  ngAfterViewInit(): void {
    const maxHandle = document.querySelector('span.ngx-slider-span.ngx-slider-pointer.ngx-slider-pointer-max');
    const minHandle = document.querySelector('span.ngx-slider-span.ngx-slider-pointer.ngx-slider-pointer-min');
    maxHandle.addEventListener('click', () => {
      this.interactions.emit({
        interactWithMaxHandle: true,
      });
    });
    minHandle.addEventListener('click', () => {
      this.interactions.emit({
        interactWithMinHandle: true,
      });
    });
  }
}
